import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import "./Membership.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function MembershipForm({ selectedPlan, onClose }) {
  const stripe   = useStripe();
  const elements = useElements();

  const [formData, setFormData] = useState({
    first_name: "", middle_name: "", last_name: "",
    dob: "", email: "", phone: "",
  });
  const [errors,    setErrors]   = useState({});
  const [loading,   setLoading]  = useState(false);
  const [cardError, setCardError] = useState("");

  useEffect(() => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, []);

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const validatePhone = (p) => /^(\+?\d{7,15})$/.test(p.replace(/[\s\-()]/g, ""));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = "First name is required";
    if (!formData.last_name.trim())  newErrors.last_name  = "Last name is required";
    if (!formData.dob)               newErrors.dob        = "Date of birth is required";
    if (!formData.email.trim())      newErrors.email      = "Email is required";
    else if (!validateEmail(formData.email)) newErrors.email = "Please enter a valid email";
    if (!formData.phone.trim())      newErrors.phone      = "Phone is required";
    else if (!validatePhone(formData.phone))
      newErrors.phone = "Please enter a valid phone (e.g. +61 400 000 000)";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || !stripe || !elements) return;
    setLoading(true);
    setCardError("");

    try {
      // ── Step 1: Validate member fields on the backend (no member created yet) ──
      try {
        await api.post("members/members/", {
          ...formData,
          plan: selectedPlan.id,
        });
      } catch (err) {
        // Surface field validation errors (duplicate email, bad phone, etc.)
        if (err.response?.data) {
          const backendErrors = err.response.data;
          if (typeof backendErrors === "object" && !backendErrors.error) {
            setErrors(backendErrors);
          } else {
            setCardError(backendErrors.error || "Validation failed. Please check your details.");
          }
        } else {
          setCardError("Validation failed. Please check your details.");
        }
        setLoading(false);
        return;
      }

      // ── Step 2: Create Stripe payment intent ──────────────────────────────
      const intentResp = await api.post("members/create-payment-intent/", {
        amount      : parseFloat(selectedPlan.price).toFixed(2),
        payment_type: "membership",
      });
      const { client_secret, payment_intent_id, charged_amount } = intentResp.data;

      // ── Step 3: Confirm card payment with Stripe ──────────────────────────
      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name : `${formData.first_name} ${formData.last_name}`,
            email: formData.email,
          },
        },
      });

      if (result.error) {
        setCardError(result.error.message);
        setLoading(false);
        return;
      }

      // ── Step 4: Payment succeeded — create member + record payment ────────
      await api.post("members/confirm-payment/", {
        payment_intent_id,
        payment_type: "membership",
        amount      : parseFloat(selectedPlan.price).toFixed(2),
        plan_id     : selectedPlan.id,
        first_name  : formData.first_name,
        middle_name : formData.middle_name,
        last_name   : formData.last_name,
        dob         : formData.dob || null,
        email       : formData.email,
        phone       : formData.phone,
      });

      alert("✅ Registration successful! Check your email to set your password.");
      window.location.href = "/login";

    } catch (err) {
      console.error(err);
      if (err.response?.data) {
        const backendErrors = err.response.data;
        if (typeof backendErrors === "object" && !backendErrors.error) {
          setErrors(backendErrors);
        } else {
          setCardError(backendErrors.error || "Payment failed. Please try again.");
        }
      } else {
        setCardError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="membership-form-overlay">
      <div className="membership-form">
        <h2>Join {selectedPlan.name}</h2>
        <p className="membership-price">One-time fee: ${selectedPlan.price} AUD</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input type="text" name="first_name" placeholder="First Name"
              value={formData.first_name} onChange={handleInputChange}
              className={errors.first_name ? "error" : ""} />
            {errors.first_name && <span className="error-message">{errors.first_name}</span>}
          </div>
          <div className="form-group">
            <input type="text" name="middle_name" placeholder="Middle Name (optional)"
              value={formData.middle_name} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <input type="text" name="last_name" placeholder="Last Name"
              value={formData.last_name} onChange={handleInputChange}
              className={errors.last_name ? "error" : ""} />
            {errors.last_name && <span className="error-message">{errors.last_name}</span>}
          </div>
          <div className="form-group">
            <label style={{ fontWeight: "bold", color: "#333" }}>Date of Birth</label>
            <input type="date" name="dob" value={formData.dob}
              onChange={handleInputChange} className={errors.dob ? "error" : ""} />
            {errors.dob && <span className="error-message">{errors.dob}</span>}
          </div>
          <div className="form-group">
            <input type="email" name="email" placeholder="Email"
              value={formData.email} onChange={handleInputChange}
              className={errors.email ? "error" : ""} />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>
          <div className="form-group">
            <input type="tel" name="phone" placeholder="Phone (e.g. +61 400 000 000)"
              value={formData.phone} onChange={handleInputChange}
              className={errors.phone ? "error" : ""} />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <h3>Payment Details</h3>
          <p className="secure-note">
            🔒 Secured by Stripe — your card details are encrypted and never stored on our servers.
          </p>
          <div className="form-group">
            <div className="stripe-card-element-wrapper">
              <CardElement options={{
                hidePostalCode: true,
                style: {
                  base:    { fontSize: "16px", color: "#333", "::placeholder": { color: "#aaa" } },
                  invalid: { color: "#e53935" },
                }
              }} />
            </div>
            {cardError && <span className="error-message">{cardError}</span>}
          </div>

          <button type="submit" disabled={loading || !stripe}>
            {loading ? "Processing…" : `Pay $${selectedPlan.price} AUD`}
          </button>
          <button type="button" className="cancel-btn" onClick={onClose} disabled={loading}>
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Membership page
// ─────────────────────────────────────────────────────────────
export default function Membership() {
  const [membershipPlans,  setMembershipPlans]  = useState([]);
  const [welfarePlans,     setWelfarePlans]     = useState([]);
  const [selectedPlan,     setSelectedPlan]     = useState(null);
  const [isLoggedIn,       setIsLoggedIn]       = useState(false);
  const [membershipPaid,   setMembershipPaid]   = useState(false);
  const [alreadyInWelfare, setAlreadyInWelfare] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    setIsLoggedIn(!!token);

    if (token) {
      api.get("members/dashboard/")
        .then(r => {
          setAlreadyInWelfare(r.data.in_welfare);
          setMembershipPaid(r.data.membership_paid);
        })
        .catch(() => {});
    }

    // Load plans; after both resolve, honour ?plan= deep-link
    Promise.all([
      api.get("members/plans/"),
      api.get("members/welfare-plans/"),
    ]).then(([mpRes, wpRes]) => {
      setMembershipPlans(mpRes.data);
      setWelfarePlans(wpRes.data);

      // Auto-open the modal if ?plan=<id> is in the URL (and user isn't logged in)
      const planParam = searchParams.get("plan");
      if (planParam && !token) {
        const match = mpRes.data.find(
          (p) => String(p.id) === planParam || p.slug === planParam
        );
        if (match) setSelectedPlan(match);
      }
    }).catch(e => console.error(e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build a shareable link for a plan card
  const shareLink = (plan) =>
    `${window.location.origin}/membership?plan=${plan.id}`;

  const copyLink = (plan) => {
    navigator.clipboard.writeText(shareLink(plan)).then(() => {
      alert(`✅ Link copied!\n${shareLink(plan)}`);
    });
  };

  return (
    <div className="membership-container">

      {/* ── Section 1: Community Membership ── */}
      <div className="membership-section">
        <div className="section-badge">Step 1</div>
        <h1>Community Membership</h1>
        <p className="membership-subtitle">
          Pay once to become a recognised member of the Marakwet Community in Australia.
          You will get access to your member dashboard, events, opportunities and welfare programmes.
        </p>

        <div className="plans-grid">
          {membershipPlans.map(plan => (
            <div key={plan.id} className="plan-card membership-plan-card">
              <div className="plan-icon">🤝</div>
              <h3>{plan.name}</h3>
              <p>{plan.description}</p>
              <h4>${plan.price} AUD</h4>
              <p className="one-time-payment">✅ One-time annual payment</p>
              {isLoggedIn ? (
                <p className="already-member-note">
                  You are already a member. Manage your account in your{" "}
                  <span className="link-text" onClick={() => navigate("/dashboard")}>
                    Dashboard
                  </span>.
                </p>
              ) : (
                <button onClick={() => setSelectedPlan(plan)}>Join Now</button>
              )}
              {/* Share link button — visible to everyone (leaders share it) */}
              <button
                className="share-link-btn"
                onClick={() => copyLink(plan)}
                title={shareLink(plan)}
              >
                🔗 Copy Join Link
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="section-divider">
        <span>Also Available to Members</span>
      </div>

      {/* ── Section 2: Welfare Plans ── */}
      <div className="membership-section">
        <div className="section-badge welfare-badge">Step 2 — Optional</div>
        <h2>Welfare Programme</h2>
        <p className="membership-subtitle">
          After becoming a paid member, you can optionally enrol in the welfare programme.
          This covers community welfare support and can be paid in full or in instalments.
        </p>

        <div className="plans-grid">
          {welfarePlans.map(plan => (
            <div key={plan.id} className="plan-card welfare-plan-card">
              <div className="plan-icon">🛡️</div>
              <h3>{plan.name}</h3>
              <p>{plan.description}</p>
              <h4>${plan.total_amount} AUD</h4>

              <div className="welfare-payment-options">
                <p className="welfare-options-label">Payment options:</p>
                <div className="welfare-options-grid">
                  <span>💰 Full payment</span>
                  <span>📅 Weekly (12 payments)</span>
                  <span>📅 Fortnightly (6 payments)</span>
                  <span>📅 Monthly (3 payments)</span>
                </div>
              </div>

              {!isLoggedIn ? (
                <div className="welfare-cta">
                  <p className="welfare-login-note">
                    You must be a paid member to join the welfare programme.
                  </p>
                  <button className="welfare-join-btn outline-btn"
                    onClick={() => navigate("/login")}>
                    Login to Enrol
                  </button>
                </div>
              ) : alreadyInWelfare ? (
                <p className="already-member-note">
                  ✅ You are already enrolled in the welfare programme.{" "}
                  <span className="link-text" onClick={() => navigate("/dashboard")}>
                    View in Dashboard
                  </span>.
                </p>
              ) : !membershipPaid ? (
                <div className="welfare-cta">
                  <div className="welfare-locked-note">
                    🔒 You must complete your membership payment before joining the welfare programme.
                  </div>
                  <button className="welfare-join-btn outline-btn"
                    onClick={() => navigate("/membership")}>
                    Pay Membership First
                  </button>
                </div>
              ) : (
                <button className="welfare-join-btn"
                  onClick={() => navigate("/dashboard")}>
                  Enrol in Dashboard
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Membership payment modal */}
      {selectedPlan && (
        <Elements stripe={stripePromise}>
          <MembershipForm
            selectedPlan={selectedPlan}
            onClose={() => {
              setSelectedPlan(null);
              // Clean up ?plan= from URL without a page reload
              setSearchParams({});
            }}
          />
        </Elements>
      )}
    </div>
  );
}